import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { learningRoadmap } from '@/mocks/mt4mt5Page';

type PlatformKey = 'mt4' | 'mt5';

const platforms: { key: PlatformKey; label: string; full: string }[] = [
  { key: 'mt4', label: 'MT4', full: 'MetaTrader 4' },
  { key: 'mt5', label: 'MT5', full: 'MetaTrader 5' },
];

export default function LearningRoadmap() {
  const [platform, setPlatform] = useState<PlatformKey>('mt4');
  const stages = learningRoadmap[platform];
  const isMt4 = platform === 'mt4';

  const dotClass = isMt4 ? 'bg-primary-500 text-background-50' : 'bg-accent-500 text-background-50';
  const pointIconClass = isMt4 ? 'text-primary-600' : 'text-accent-600';

  return (
    <section id="roadmap" className="scroll-mt-24 border-b border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-10">
          <SectionHeading
            eyebrow="เส้นทางการเรียนรู้"
            title="เริ่มจากติดตั้งไปจนถึงออเดอร์แรก"
            subtitle="สี่ขั้นตอนที่ควรทำตามลำดับ ตั้งแต่ติดตั้งโปรแกรม ไปจนถึงเปิดคำสั่งซื้อขายบนบัญชีทดลอง เลือกแพลตฟอร์มที่คุณใช้อยู่ด้านขวา"
          />

          <div
            role="tablist"
            aria-label="เลือกแพลตฟอร์มสำหรับเส้นทางการเรียนรู้"
            className="inline-flex w-fit shrink-0 items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1"
          >
            {platforms.map((item) => {
              const active = item.key === platform;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setPlatform(item.key)}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    active
                      ? item.key === 'mt4'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-accent-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  <i className="ri-computer-line text-sm" aria-hidden="true" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <ol className="mt-10 flex flex-col gap-4 md:mt-12 md:gap-5">
          {stages.map((stage, index) => (
            <li key={`${platform}-${stage.step}`} className="relative pl-12 md:pl-16">
              {index !== stages.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-[-1.25rem] left-[17px] top-10 w-px bg-background-300 md:left-[21px] md:top-12"
                />
              )}
              <span
                aria-hidden="true"
                className={`absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-full font-num text-[13px] font-semibold md:h-11 md:w-11 md:text-sm ${dotClass}`}
              >
                {stage.step}
              </span>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-8">
                  <div className="md:col-span-5">
                    <p className="text-[11px] font-semibold text-foreground-500">
                      ขั้นที่ {index + 1} · {isMt4 ? 'MetaTrader 4' : 'MetaTrader 5'}
                    </p>
                    <h3 className="mt-1.5 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                      {stage.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{stage.summary}</p>
                  </div>

                  <ul className="flex flex-col gap-2.5 md:col-span-7">
                    {stage.points.map((point) => (
                      <li key={point} className="flex items-start gap-2.5">
                        <i className={`ri-check-line mt-0.5 text-sm ${pointIconClass}`} aria-hidden="true" />
                        <span className="text-sm leading-relaxed text-foreground-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
          <i className={`ri-route-line mt-0.5 text-base ${pointIconClass}`} aria-hidden="true" />
          <p className="text-xs leading-relaxed text-foreground-600 md:text-sm">
            ลำดับนี้ใช้ได้กับทั้งสองแพลตฟอร์ม ต่างกันแค่ตำแหน่งเมนูและจำนวนเครื่องมือ
            หลังทำครบสี่ขั้นแล้ว ค่อยกลับไปดูรายละเอียดแต่ละหัวข้อในคู่มือด้านบน
          </p>
        </div>
      </div>
    </section>
  );
}